/**
 * Flow intent validation — treats all AI/deterministic engine output as hostile input.
 * Parse → normalize → Zod schema-check → return typed result or null.
 * Aligned with OWASP LLM02 (insecure output handling) and LLM08 (excessive agency).
 */

import { z } from 'zod';

const ContactStageSchema = z.enum([
  'new',
  'qualified',
  'proposal',
  'negotiation',
  'closed_won',
  'closed_lost',
]);

export const FlowSuggestionSchema = z.object({
  type: z.enum(['move_stage', 'follow_up', 'high_priority']),
  contactId: z.string().min(1).max(128),
  contactName: z.string().min(1).max(200),
  reason: z.string().min(1).max(500),
  targetStage: ContactStageSchema.optional(),
  urgency: z.enum(['low', 'medium', 'high']),
});

export type FlowSuggestion = z.infer<typeof FlowSuggestionSchema>;

export const EmailDraftSchema = z.object({
  contactId: z.string().min(1).max(128),
  contactName: z.string().min(1).max(200),
  contactEmail: z.string().email(),
  subject: z.string().min(1).max(200),
  preview: z.string().min(1).max(300),
  body: z.string().min(1).max(5000),
  ctaSuggestion: z.string().max(200),
});

export type EmailDraft = z.infer<typeof EmailDraftSchema>;

/** Validate raw suggestion data — returns null and logs on failure. */
export function validateSuggestion(raw: unknown): FlowSuggestion | null {
  const result = FlowSuggestionSchema.safeParse(raw);
  if (!result.success) {
    console.warn('[Flow] Suggestion rejected:', result.error.flatten());
    return null;
  }
  return result.data;
}

/** Validate raw email draft data — returns null and logs on failure. */
export function validateEmailDraft(raw: unknown): EmailDraft | null {
  const result = EmailDraftSchema.safeParse(raw);
  if (!result.success) {
    console.warn('[Flow] Email draft rejected:', result.error.flatten());
    return null;
  }
  return result.data;
}

/**
 * Strip characters that could cause XSS in rendered UI output.
 * Applied to all strings before display.
 */
export function sanitizeFlowString(input: string, maxLength = 500): string {
  return String(input)
    .replace(/[<>'"&]/g, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+=/gi, '')
    .trim()
    .slice(0, maxLength);
}

/**
 * Validate that a contactId is a safe identifier (no injection).
 * Matches UUID format or short alphanumeric IDs used by the backend.
 */
export function isValidContactId(id: unknown): id is string {
  if (typeof id !== 'string') return false;
  return /^[a-zA-Z0-9_\-]{1,128}$/.test(id);
}
